'use client';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import { FlashList } from '@ps/ui/organisms/flash-list';
import { Loader2 } from 'lucide-react';

import { useCommunityMessageContext } from '../../contexts/community-message/useCommunityMessageContext';
import {
  type MessageListEntry,
  groupMessagesByDate,
} from '../../functions/utils/groupMessagesByDate';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import type { MessageInteractionMode } from '../../types/MessageInteractionMode';
import { ChannelMessageBubble } from '../channel-message-view/ChannelMessageBubble';

interface Props {
  currentUserId: string | null | undefined;
  /**
   * Channel slug threaded through to each bubble so the
   * `Reply` / `View Replies` links know how to construct the thread
   * URL. The slug isn't on the message doc itself (only `channelId`
   * is), so the caller passes it explicitly.
   */
  channelSlug?: string;
  /** The viewer's interaction rights, forwarded to each bubble. */
  mode?: MessageInteractionMode;
  /**
   * True when the pinned-messages banner overlays the top of the list — the
   * header reserves extra top space so the first message clears the banner
   * instead of sitting permanently underneath it.
   */
  hasPinnedBanner?: boolean;
}

/**
 * Recycling message list for the open channel. Reads `messages$` + `state$`
 * from the surrounding `CommunityMessageProvider` (newest-first; flipped to
 * chronological for top-to-bottom display).
 *
 * Rendered via {@link FlashList} (non-inverted, top-aligned) so only the
 * visible + overscan window of bubbles is mounted — which bounds the per-message
 * Firestore listeners each bubble opens ({@link useLiveMessage} +
 * `useMyMessageReactions`) to the viewport instead of every loaded message.
 *
 * Scroll behavior (all handled by FlashList's `maintainVisibleContentPosition`):
 * - **Short history** — top-aligned (messages fill from the top down) via
 *   `pinShortContentToBottom: false`.
 * - **Long history (has a scrollbar)** — opens at the latest message
 *   (`startRenderingFromBottom`, re-snapping as rows measure).
 * - **New message at the bottom** — autoscrolls when the viewer is near the
 *   bottom, else leaves them in place (`autoscrollToBottomThreshold`).
 * - **Older page prepended (loadMore)** — the visible anchor is preserved;
 *   `onStartReached` (scrolling up) triggers it.
 *
 * Date separators are interleaved as their own data rows (sticky headers aren't
 * available with the recycler), branched in `renderItem` via `getItemType`.
 */
export function ChannelMessageList({
  currentUserId,
  channelSlug,
  mode,
  hasPinnedBanner,
}: Props) {
  const { messages$, state$, loadMore } = useCommunityMessageContext();
  const [messages] = useBehaviorSubject(messages$);
  const [state] = useBehaviorSubject(state$);

  if (state.isLoading && messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <span className="text-content-4 text-xs">Loading…</span>
      </div>
    );
  }

  if (state.error && messages.length === 0) {
    return (
      <p
        role="alert"
        className="bg-danger/10 m-4 rounded-md border border-red-500/30 px-4 py-3 text-sm text-red-200"
      >
        Couldn&rsquo;t load messages.{' '}
        {state.error.message ?? 'Please try again.'}
      </p>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 px-6 text-center">
        <p className="text-content-1 text-sm">No messages yet</p>
        <p className="text-content-3 text-xs">
          Say hello to start the conversation.
        </p>
      </div>
    );
  }

  // `messages` keeps newest-first; flip for chronological (top→bottom) render.
  // Dedupe by the unique server `id` first — a broadcast post can surface the
  // same message twice (pagination overlap / a shared `clientMessageId`).
  const ordered = dedupeById([...messages].reverse());
  const entries = groupMessagesByDate(ordered);

  const onStartReached = () => {
    if (!state.hasMore || state.isLoadingMore || state.isLoading) return;
    loadMore();
  };

  return (
    <FlashList<MessageListEntry>
      data={entries}
      className="min-h-0 flex-1"
      defaultItemSize={96}
      getItemType={entry => entry.kind}
      keyExtractor={entry =>
        entry.kind === 'message' ? entry.message.id : `d-${entry.key}`
      }
      onStartReached={onStartReached}
      onStartReachedThreshold={0.5}
      maintainVisibleContentPosition={{
        startRenderingFromBottom: true,
        pinShortContentToBottom: false,
        autoscrollToBottomThreshold: 0.2,
      }}
      // Padding lives on the header/footer/items — NOT on the content
      // container: FlashList positions each row at the scroller's full width,
      // so horizontal content padding would push rows past the right edge and
      // clip them. Header doubles as the top pad + "loading older" spinner.
      ListHeaderComponent={
        <div
          className={`flex items-center justify-center ${hasPinnedBanner ? 'pt-14' : 'pt-4'}`}
        >
          {state.isLoadingMore ? (
            <Loader2 className="text-content-4 size-4 animate-spin" />
          ) : null}
        </div>
      }
      ListFooterComponent={<div className="h-4" />}
      renderItem={({ item }) =>
        item.kind === 'date-separator' ? (
          <div className="px-4">
            <DateSeparator label={item.label} />
          </div>
        ) : (
          <div className="px-4 pb-2">
            <ChannelMessageBubble
              message={item.message}
              currentUserId={currentUserId}
              channelSlug={channelSlug}
              mode={mode}
            />
          </div>
        )
      }
    />
  );
}

function dedupeById(
  messages: ExtendedCommunityChannelMessage[],
): ExtendedCommunityChannelMessage[] {
  const seen = new Set<string>();
  const out: ExtendedCommunityChannelMessage[] = [];
  for (const message of messages) {
    if (seen.has(message.id)) continue;
    seen.add(message.id);
    out.push(message);
  }
  return out;
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="bg-surface-2 h-px flex-1" />
      <span className="bg-surface-1 text-content-3 rounded-full px-3 py-1 text-xs">
        {label}
      </span>
      <span className="bg-surface-2 h-px flex-1" />
    </div>
  );
}
